import React, { useCallback } from 'react'

type Props = Readonly<{
  id?: string
  className?: string
  disabled?: boolean
  value: string
  onChangeValue: (nextValue: string) => void
  pickerOnly?: boolean
  placeholder?: string
  style?: React.CSSProperties
}>

const isPrintableKey = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
  if (e.key.length === 1) return true
  if (e.key === 'Backspace' || e.key === 'Delete') return true
  return false
}

const isAllowedControlKey = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
  const allowed = new Set([
    'Tab',
    'Enter',
    'Escape',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End'
  ])

  if (allowed.has(e.key)) return true
  if (e.ctrlKey || e.metaKey) return true
  return false
}

export default function DateInput({
  id,
  className,
  disabled,
  value,
  onChangeValue,
  pickerOnly = true,
  placeholder,
  style
}: Props) {
  const tryOpenPicker = useCallback((input: HTMLInputElement) => {
    if (!pickerOnly) return
    const anyInput = input as any
    if (typeof anyInput.showPicker === 'function') {
      try {
        anyInput.showPicker()
      } catch {
        // ignore
      }
    }
  }, [pickerOnly])

  return (
    <input
      id={id}
      type="date"
      className={className}
      disabled={disabled}
      value={value}
      inputMode={pickerOnly ? 'none' : undefined}
      onPointerDown={(e) => {
        tryOpenPicker(e.currentTarget)
      }}
      onKeyDown={(e) => {
        if (!pickerOnly) return
        if (isAllowedControlKey(e)) return
        if (isPrintableKey(e)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      onBeforeInput={(e) => {
        if (!pickerOnly) return
        e.preventDefault()
      }}
      onPaste={(e) => {
        if (!pickerOnly) return
        e.preventDefault()
      }}
      onChange={(e) => onChangeValue(e.target.value)}
      placeholder={placeholder}
      style={style}
    />
  )
}
